import { useEffect, useState, useCallback } from 'react';
import {
    Container, Typography, Card, Stack, Box, Button, Chip, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, MenuItem, Drawer, Alert, InputAdornment, Tooltip,
    CircularProgress, Grid, Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import personAddFill from '@iconify/icons-eva/person-add-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import shieldFill from '@iconify/icons-eva/shield-fill';
import lockFill from '@iconify/icons-eva/lock-fill';
import unlockFill from '@iconify/icons-eva/unlock-fill';
import Page from 'src/components/Page';
import { adminSyndicationApi } from 'src/utils/syndicationApi';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';

const STATUS_COLORS = {
    pending_email_verification: 'default',
    pending_approval: 'warning',
    approved: 'info',
    active: 'success',
    suspended: 'error',
    rejected: 'error',
};

const STATUS_LABELS = {
    pending_email_verification: 'Pending Verification',
    pending_approval: 'Pending Approval',
    approved: 'Approved (2FA Pending)',
    active: 'Active',
    suspended: 'Suspended',
    rejected: 'Rejected',
};

export default function AdminInvestorList() {
    const [investors, setInvestors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [apiError, setApiError] = useState('');

    // Drawers
    const [rejectDrawer, setRejectDrawer] = useState({ open: false, investorId: null });
    const [suspendDrawer, setSuspendDrawer] = useState({ open: false, investorId: null });
    const [createDrawer, setCreateDrawer] = useState(false);
    const [reason, setReason] = useState('');
    const [actionLoading, setActionLoading] = useState(null);

    // Detail drawer
    const [detailDrawer, setDetailDrawer] = useState({ open: false, investor: null, loading: false });

    // Reset 2FA confirmation drawer
    const [reset2FADrawer, setReset2FADrawer] = useState({ open: false, investor: null });

    // Create investor form
    const [newInvestor, setNewInvestor] = useState({
        first_name: '', last_name: '', email: '', phone: '', investor_type: '',
    });

    const fetchInvestors = useCallback(() => {
        setLoading(true);
        setApiError('');
        const params = new URLSearchParams();
        params.append('page', page + 1);
        params.append('page_size', rowsPerPage);
        if (search) params.append('search', search);
        if (statusFilter) params.append('status', statusFilter);

        adminSyndicationApi.get(`admin/investors/?${params.toString()}`)
            .then((response) => {
                const data = response.data;
                setInvestors(data?.data?.data || data?.data || []);
                setTotalCount(data?.data?.count || data?.count || 0);
            })
            .catch((error) => {
                setApiError(error.response?.data?.message || 'Failed to load investors.');
            })
            .finally(() => setLoading(false));
    }, [page, rowsPerPage, search, statusFilter]);

    useEffect(() => { fetchInvestors(); }, [fetchInvestors]);

    const handleApprove = (id) => {
        if (actionLoading) return;
        setActionLoading(id);
        adminSyndicationApi.post(`admin/investors/${id}/approve/`)
            .then(() => {
                showToast('Investor approved. 2FA setup email sent.', 'success');
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error))
            .finally(() => setActionLoading(null));
    };

    const handleReject = () => {
        if (!reason.trim()) return;
        adminSyndicationApi.post(`admin/investors/${rejectDrawer.investorId}/reject/`, { reason })
            .then(() => {
                showToast('Investor rejected.', 'success');
                setRejectDrawer({ open: false, investorId: null });
                setReason('');
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error));
    };

    const handleSuspend = () => {
        if (!reason.trim()) return;
        adminSyndicationApi.post(`admin/investors/${suspendDrawer.investorId}/suspend/`, { reason })
            .then(() => {
                showToast('Investor suspended. All sessions terminated.', 'success');
                setSuspendDrawer({ open: false, investorId: null });
                setReason('');
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error));
    };

    const handleReactivate = (id) => {
        adminSyndicationApi.post(`admin/investors/${id}/reactivate/`)
            .then(() => {
                showToast('Investor reactivated.', 'success');
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error));
    };

    const handleReset2FA = () => {
        if (!reset2FADrawer.investor) return;
        adminSyndicationApi.post(`admin/investors/${reset2FADrawer.investor.id}/reset-2fa/`)
            .then(() => {
                showToast('2FA reset. Setup email sent.', 'success');
                setReset2FADrawer({ open: false, investor: null });
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error));
    };

    const handleViewDetail = (id) => {
        setDetailDrawer({ open: true, investor: null, loading: true });
        adminSyndicationApi.get(`admin/investors/${id}/`)
            .then((response) => {
                setDetailDrawer({ open: true, investor: response.data?.data, loading: false });
            })
            .catch((error) => {
                showErrorToast(error);
                setDetailDrawer({ open: false, investor: null, loading: false });
            });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const handleCreateInvestor = () => {
        adminSyndicationApi.post('admin/investors/create/', newInvestor)
            .then(() => {
                showToast('Investor created. Welcome email sent.', 'success');
                setCreateDrawer(false);
                setNewInvestor({ first_name: '', last_name: '', email: '', phone: '', investor_type: '' });
                fetchInvestors();
            })
            .catch((error) => showErrorToast(error));
    };

    return (
        <Page title="Investors | Syndication Admin">
            <Container maxWidth="xl">
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h4">Investor Management</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Icon icon={personAddFill} />}
                        onClick={() => setCreateDrawer(true)}
                    >
                        Create Investor
                    </Button>
                </Stack>

                {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

                {/* Filters */}
                <Card sx={{ mb: 3, p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Search by name, email, organization..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            sx={{ minWidth: 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon={searchFill} width={20} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                            sx={{ minWidth: 180 }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                        </TextField>
                        <IconButton onClick={fetchInvestors}>
                            <Icon icon={refreshFill} width={20} />
                        </IconButton>
                    </Stack>
                </Card>

                {/* Table */}
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Path</TableCell>
                                    <TableCell>2FA</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : investors.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No investors found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    investors.map((inv) => (
                                        <TableRow key={inv.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewDetail(inv.id)}>
                                            <TableCell>
                                                <Typography variant="subtitle2">
                                                    {inv.first_name} {inv.last_name}
                                                </Typography>
                                                {inv.organization_name && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {inv.organization_name}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{inv.email}</TableCell>
                                            <TableCell>{inv.phone}</TableCell>
                                            <TableCell>
                                                {inv.investor_type ? inv.investor_type.replace(/_/g, ' ') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={STATUS_LABELS[inv.status] || inv.status}
                                                    color={STATUS_COLORS[inv.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={inv.registration_path === 'self' ? 'Self' : 'Admin'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={inv.totp_enabled ? 'On' : 'Off'}
                                                    color={inv.totp_enabled ? 'success' : 'default'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View Details">
                                                        <IconButton onClick={() => handleViewDetail(inv.id)}>
                                                            <Icon icon={eyeFill} width={20} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {inv.status === 'pending_approval' && (
                                                        <>
                                                            <Tooltip title="Approve">
                                                                <IconButton color="success" onClick={() => handleApprove(inv.id)} disabled={actionLoading === inv.id}>
                                                                    <Icon icon={checkmarkCircle2Fill} width={20} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton color="error" onClick={() => {
                                                                    setRejectDrawer({ open: true, investorId: inv.id });
                                                                    setReason('');
                                                                }}>
                                                                    <Icon icon={closeCircleFill} width={20} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {inv.status === 'active' && (
                                                        <>
                                                            <Tooltip title="Suspend">
                                                                <IconButton color="warning" onClick={() => {
                                                                    setSuspendDrawer({ open: true, investorId: inv.id });
                                                                    setReason('');
                                                                }}>
                                                                    <Icon icon={lockFill} width={20} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reset 2FA">
                                                                <IconButton color="info" onClick={() => setReset2FADrawer({ open: true, investor: inv })}>
                                                                    <Icon icon={shieldFill} width={20} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                    {inv.status === 'suspended' && (
                                                        <Tooltip title="Reactivate">
                                                            <IconButton color="success" onClick={() => handleReactivate(inv.id)}>
                                                                <Icon icon={unlockFill} width={20} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                </Card>

                {/* Detail Drawer */}
                <Drawer anchor="right" open={detailDrawer.open} onClose={() => setDetailDrawer({ open: false, investor: null, loading: false })} PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 560 } } }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Investor Details</Typography>
                                {detailDrawer.investor && (
                                    <Chip
                                        label={STATUS_LABELS[detailDrawer.investor.status] || detailDrawer.investor.status}
                                        color={STATUS_COLORS[detailDrawer.investor.status] || 'default'}
                                        size="small"
                                    />
                                )}
                            </Stack>
                            <IconButton onClick={() => setDetailDrawer({ open: false, investor: null, loading: false })} size="small">
                                <Icon icon={closeFill} width={20} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            {detailDrawer.loading ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                            ) : detailDrawer.investor && (() => {
                                const inv = detailDrawer.investor;
                                return (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Basic Information</Typography><Divider /></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">First Name</Typography><Typography variant="body2">{inv.first_name || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Last Name</Typography><Typography variant="body2">{inv.last_name || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2">{inv.email || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2">{inv.phone || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Organization</Typography><Typography variant="body2">{inv.organization_name || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Investor Type</Typography><Typography variant="body2">{inv.investor_type ? inv.investor_type.replace(/_/g, ' ') : '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Country</Typography><Typography variant="body2">{inv.country_name || '-'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Registration Path</Typography><Typography variant="body2">{inv.registration_path === 'self' ? 'Self-Registered' : 'Admin-Created'}</Typography></Grid>

                                        <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" color="text.secondary">Account Status</Typography><Divider /></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Status</Typography><Typography variant="body2">{STATUS_LABELS[inv.status] || inv.status}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">2FA Enabled</Typography><Typography variant="body2">{inv.totp_enabled ? 'Yes' : 'No'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">NDA Accepted</Typography><Typography variant="body2">{inv.nda_accepted ? 'Yes' : 'No'}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">NDA Accepted At</Typography><Typography variant="body2">{formatDate(inv.nda_accepted_at)}</Typography></Grid>

                                        {inv.approved_by_name && (
                                            <>
                                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Approved By</Typography><Typography variant="body2">{inv.approved_by_name}</Typography></Grid>
                                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Approved At</Typography><Typography variant="body2">{formatDate(inv.approved_at)}</Typography></Grid>
                                            </>
                                        )}
                                        {inv.rejected_by_name && (
                                            <>
                                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Rejected By</Typography><Typography variant="body2">{inv.rejected_by_name}</Typography></Grid>
                                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Rejected At</Typography><Typography variant="body2">{formatDate(inv.rejected_at)}</Typography></Grid>
                                                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Rejection Reason</Typography><Typography variant="body2">{inv.rejection_reason || '-'}</Typography></Grid>
                                            </>
                                        )}
                                        {inv.suspension_reason && (
                                            <>
                                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Suspended At</Typography><Typography variant="body2">{formatDate(inv.suspended_at)}</Typography></Grid>
                                                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Suspension Reason</Typography><Typography variant="body2">{inv.suspension_reason}</Typography></Grid>
                                            </>
                                        )}

                                        <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" color="text.secondary">Timestamps</Typography><Divider /></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Registered</Typography><Typography variant="body2">{formatDate(inv.created)}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Last Updated</Typography><Typography variant="body2">{formatDate(inv.modified)}</Typography></Grid>
                                    </Grid>
                                );
                            })()}
                        </Box>
                    </Box>
                </Drawer>

                {/* Reject Drawer */}
                <Drawer anchor="right" open={rejectDrawer.open} onClose={() => setRejectDrawer({ open: false, investorId: null })} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Reject Investor</Typography>
                            <IconButton onClick={() => setRejectDrawer({ open: false, investorId: null })} size="small">
                                <Icon icon={closeFill} width={20} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, p: 2 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Rejection Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Provide a reason for rejection..."
                            />
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setRejectDrawer({ open: false, investorId: null })}>Cancel</Button>
                            <Button variant="contained" color="error" onClick={handleReject} disabled={!reason.trim()}>
                                Reject
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                {/* Suspend Drawer */}
                <Drawer anchor="right" open={suspendDrawer.open} onClose={() => setSuspendDrawer({ open: false, investorId: null })} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Suspend Investor</Typography>
                            <IconButton onClick={() => setSuspendDrawer({ open: false, investorId: null })} size="small">
                                <Icon icon={closeFill} width={20} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, p: 2 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This will immediately terminate all active sessions for this investor.
                            </Alert>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Suspension Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Provide a reason for suspension..."
                            />
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setSuspendDrawer({ open: false, investorId: null })}>Cancel</Button>
                            <Button variant="contained" color="warning" onClick={handleSuspend} disabled={!reason.trim()}>
                                Suspend
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                {/* Create Investor Drawer */}
                <Drawer anchor="right" open={createDrawer} onClose={() => setCreateDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Create Investor</Typography>
                            <IconButton onClick={() => setCreateDrawer(false)} size="small">
                                <Icon icon={closeFill} width={20} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        value={newInvestor.first_name}
                                        onChange={(e) => setNewInvestor({ ...newInvestor, first_name: e.target.value })}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        value={newInvestor.last_name}
                                        onChange={(e) => setNewInvestor({ ...newInvestor, last_name: e.target.value })}
                                    />
                                </Stack>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={newInvestor.email}
                                    onChange={(e) => setNewInvestor({ ...newInvestor, email: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={newInvestor.phone}
                                    onChange={(e) => setNewInvestor({ ...newInvestor, phone: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    select
                                    label="Investor Type"
                                    value={newInvestor.investor_type}
                                    onChange={(e) => setNewInvestor({ ...newInvestor, investor_type: e.target.value })}
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    <MenuItem value="individual">Individual / Angel</MenuItem>
                                    <MenuItem value="family_office">Family Office</MenuItem>
                                    <MenuItem value="institutional">Institutional</MenuItem>
                                    <MenuItem value="corporate">Corporate / Strategic</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </TextField>
                            </Stack>
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setCreateDrawer(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleCreateInvestor}
                                disabled={!newInvestor.first_name || !newInvestor.last_name || !newInvestor.email || !newInvestor.phone}
                            >
                                Create & Send Invite
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                {/* Reset 2FA Confirmation Drawer */}
                <Drawer anchor="right" open={reset2FADrawer.open} onClose={() => setReset2FADrawer({ open: false, investor: null })} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Reset 2FA</Typography>
                            <IconButton onClick={() => setReset2FADrawer({ open: false, investor: null })} size="small">
                                <Icon icon={closeFill} width={20} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, p: 2 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This will reset two-factor authentication for this investor. They will need to set up 2FA again before they can log in.
                            </Alert>
                            {reset2FADrawer.investor && (
                                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Investor</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {reset2FADrawer.investor.first_name} {reset2FADrawer.investor.last_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {reset2FADrawer.investor.email}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setReset2FADrawer({ open: false, investor: null })}>Cancel</Button>
                            <Button variant="contained" color="warning" onClick={handleReset2FA}>
                                Reset 2FA
                            </Button>
                        </Box>
                    </Box>
                </Drawer>
            </Container>
        </Page>
    );
}
